import {
  ReactElement, Suspense, useEffect, useState,
} from 'react';
import { Col, Row } from 'antd';
import styled from 'styled-components';
import {
  useDispatch, useSelector,
} from 'react-redux';

import {
  actions as feedActions,
} from 'Slice/FeedActivitiesSlice';
import { selectFeedBoardPulseData, selectFeedBoardPulseLoading } from 'Selector/FeedActivitiesSelector';
import { CompaniesPulse, Heatmap } from 'Types/FeedActivitiesTypes';
import _ from 'lodash';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import classNames from 'classnames';
import HeatMapTooltipItem from 'Components/common/components/HeatMapTooltipItem';
import HeadingWithToolTip from 'Components/common/components/HeadingWithToolTip';
import { TOOLTIP_PULSE, VIDEO_PULSE } from 'Utils/Constants';
import { IsMobile } from 'Utils/UtilityFunctions';
import {
  CaretDownOutlined,
  CaretUpOutlined,
} from '@ant-design/icons';

import { actions as certificationActions } from 'Slice/CertificationSlice';

import { selectData } from 'Selector/CertificationSelector';
import {
  boxShadow, maxMobile, maxTablet,
  minWidthApp,
  primaryColor, primaryFont, secondaryFont, wMedium,
} from '../../../Utils/utilsStyle';
import HeatMapItem from 'Components/common/components/HeatMapItem';
import TriggerBulletWidget from 'Components/common/components/TriggerBulletWidget';
import NoPersonsFound from 'Components/common/components/NoPersonsFound';

const Wrapper = styled.div`
 height: 670px;
 box-sizing: border-box;
 box-shadow : ${boxShadow};
 background-color: #fff;
 border-radius: 12px;
 padding: 16px 24px;
 @media all and (max-width: ${maxTablet}) {
  padding: 16px 10px;
 }
 @media all and (max-width: ${maxMobile}) {
  height: auto;
  margin-top: 20px;
  padding: 16px 10px 0px;
 }
 .pulse-row {
  @media all and (max-width: ${maxTablet}) {
      height: calc(100% - 70px);
  }
 }
  .heading-inner-page {
    @media all and (max-width: ${maxTablet}) {
      flex-flow: column;
      align-items: flex-start;
    }
    .tabs-wrapper {
      @media all and (max-width: ${maxTablet}) {
       margin-top: 10px;
      }
    }
  }
  .collapse-heading {
    display: flex;
    justify-content: center;
    font-family: ${secondaryFont};
    font-weight: ${wMedium};
    font-size: 14px;
    line-height: 15px;
    color: ${primaryColor};
    cursor: pointer;
    @media all and (max-width: ${maxMobile}) {
      margin-block: 16px;
    }
    .anticon {
      margin-left: 5px;
    }
  }
 .ant-pulse-triggers {
   margin-top: 20px;
    .ant-col-triggers {
      flex: 0 0 20%;
      max-width: 20%;
      @media all and (max-width: ${maxTablet}) {
        flex: 0 0 20%;
        max-width: 20%;
      }
    }
  }
`;

const SubHeading = styled.span`
  font-size: 16px;
  line-height: 20px;
  font-family: ${primaryFont};
  font-weight: ${wMedium};
  color: #67696A;
  margin-bottom: 9px;
  display: inline-block;
`;

const HeatMapRow = styled.div`
  display: flex;
  height: 7px;
  border-radius: 8px;
  .tooltip-item { 
    &:first-child {   
      border-radius: 5px 0px 0px 5px; 
    }  
    &:last-child {  
      border-radius: 0px 5px 5px 0px; 
    }
  }
`;

const PulseWrap = styled.div`
  &.flexProps{
    display: flex;
    flex-direction: column;
  }
`;

const pulseTypes = [
  {
    label: '24 Hrs',
    heatMapType: 'heatmap1',
  },
  {
    label: 'One Week',
    heatMapType: 'heatmap7',
  },
  {
    label: 'One Month',
    heatMapType: 'heatmap30',
  },
];
type Props = {
  withoutLeaderBoard?: boolean,
} & typeof defaultProps;

const defaultProps = {
  withoutLeaderBoard: false,
};
const PulseBoard = function PulseBoard(props: Props) {
  const { withoutLeaderBoard } = props;
  const dispatch = useDispatch();
  const [activePulseType, setActivePulseType] = useState(0);
  const selectedFeedBoardPulseData: CompaniesPulse = useSelector(selectFeedBoardPulseData);
  const selectedFeedBoardPulseLoading: boolean = useSelector(selectFeedBoardPulseLoading);
  const isMob = IsMobile();
  const [collapse, setCollapse] = useState(false);
  const flagForLeaderBoard: boolean = (withoutLeaderBoard && !isMob);

  useEffect(() => {
    dispatch(feedActions.getFeedPulseBoard({}));
  }, []);

  const {
    title = 'Pulse', data: { alerts } = {},
  } = selectedFeedBoardPulseData;

  const getHeatMapComponent = (alertsFromHeatMap: object) => {
    const HeatMapTools: ReactElement[] = [];
    _.forOwn(
      alertsFromHeatMap,
      (value, key) => {
        const filteredAlerts = alerts?.filter((alert) => `alert${alert.alertId}` === key);
        let color = '';
        let tip = '';

        if (filteredAlerts?.length) {
          color = filteredAlerts[0].color;
          tip = `${filteredAlerts[0].alert} (${(value * 1).toFixed(2)}%)`;
        }

        if (tip && color && value) {
          HeatMapTools.push(
            <HeatMapTooltipItem
              tooltipText={tip}
              color={color}
              width={`${value * 1}%`}
            />,
          );
        }
      },
    );
    return HeatMapTools;
  };

  const { heatMapType } = pulseTypes[activePulseType];
  const activeHeatMap = selectedFeedBoardPulseData?.data ? selectedFeedBoardPulseData?.data[
    `${heatMapType}` as keyof typeof selectedFeedBoardPulseData.data
  ] as Heatmap[] : [] as Heatmap[];

  const data = useSelector(selectData);

  const handleClick = (index: number) => {
    if (index !== activePulseType) {
      dispatch(certificationActions.certificationAction({
        type: 'pulse_filter',
        source: 'pulse',
        data,
      }));
    }
    setActivePulseType(index);
  };

  return (
    <Wrapper className={classNames('pulse-wrapper', { withoutLeaderBoardClass: flagForLeaderBoard })}>
      <div className="heading-inner-page mb-c-10" id="certification-activity">
        <h1 className="heading d-flex align-items-center">
          {title}
          <HeadingWithToolTip
            heightIconXSm="17px"
            toolTipText={TOOLTIP_PULSE}
            videoLink={VIDEO_PULSE}
          />
        </h1>
        <div className="tabs-wrapper">
          {
            selectedFeedBoardPulseLoading ? [1, 2, 3].map((val) => (
              <span className="resolution-fix pulse-board-tabs">
                <SkeletonCustom
                  height="55px"
                  width="110px"
                  diameter="10px"
                  margin="0 13px 0 0"
                  key={val}
                />
              </span>
            )) : pulseTypes.map(({ label }, index) => (
              <span
                className={`tabs-item c-pointer ${activePulseType === index ? 'active' : ''}`}
                key={label}
                onClick={() => {
                  handleClick(index);
                }}
                onKeyPress={() => {
                  handleClick(index);
                }}
                role="button"
                tabIndex={0}
                aria-label={label}
              >
                {label}
              </span>

            ))
          }
        </div>
      </div>
      <Row className="pulse-row">
        <Col span={24} className="pulse-col">

          {
            selectedFeedBoardPulseLoading ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(
              (val) => (
                <span className="resolution-fix pulse-fix">
                  <SkeletonCustom height="22px" width="calc(100% - 50px)" diameter="10px" margin="0px" key={val} />
                </span>
              ),
            ) : <SubHeading className="account-text">Accounts sorted by Sales Trigger activity</SubHeading>
          }
          <PulseWrap
            className={classNames('pulse-wrap', {
              flexProps:
                !selectedFeedBoardPulseLoading
                && activeHeatMap?.length > 8,
            })}
          >
            {activeHeatMap?.length && !selectedFeedBoardPulseLoading ? activeHeatMap.map(({
              name, id, alerts: alertsFromHeatMap, articlesCount,
            }) => (
              <Suspense
                fallback={
                  <div />
                }
              >
                <HeatMapItem key={id} heading={name || ''} count={articlesCount} id={id}>
                  <HeatMapRow className="heatmap-row">
                    {alertsFromHeatMap ? getHeatMapComponent(alertsFromHeatMap) : null}
                  </HeatMapRow>
                </HeatMapItem>
              </Suspense>
            )) : !selectedFeedBoardPulseLoading && (
              <Suspense
                fallback={
                  <div />
                }
              >
                <NoPersonsFound title="No Results Found" />
              </Suspense>
            )}
          </PulseWrap>
        </Col>
        <Col span={24} className="triggers-col">
          {flagForLeaderBoard ? (
            <SubHeading>Sales Triggers</SubHeading>
          ) : null}
          {isMob && !collapse ? (
            <span
              tabIndex={0}
              role="button"
              onClick={() => setCollapse(true)}
              onKeyPress={() => setCollapse(true)}
              className="collapse-heading"
            >
              View Triggers
              <CaretDownOutlined color={primaryColor} />
            </span>
          ) : null}
          {isMob && !collapse ? null : (
            <Row className="ant-pulse-triggers">
              {
                selectedFeedBoardPulseLoading ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((val) => (
                  <Col className="ant-col-triggers mb-c-10">
                    <SkeletonCustom height="25px" width="110px" diameter="10px" margin="0px" key={val} />
                  </Col>
                )) : null
              }
              {
                !selectedFeedBoardPulseLoading
                  ? alerts?.slice(0, alerts.length)
                    .map(({ alert, color }) => (
                      <Col className="ant-col-triggers">
                        <Suspense
                          fallback={
                            <div />
                          }
                        >
                          <TriggerBulletWidget color={color} title={alert} showTooltip />
                        </Suspense>
                        <div key={alert} />
                      </Col>
                    )) : null
              }
            </Row>
          )}
          {isMob && collapse ? (
            <span
              tabIndex={0}
              role="button"
              onClick={() => setCollapse(false)}
              onKeyPress={() => setCollapse(false)}
              className="collapse-heading"
            >
              Hide
              <CaretUpOutlined color={primaryColor} />
            </span>
          ) : null}
        </Col>
      </Row>
    </Wrapper>
  );
};
PulseBoard.defaultProps = defaultProps;
export default PulseBoard;
